import { create } from 'zustand';
import { ApplyCorrectionRequest, SpeakerCorrectionRecord } from '../types/speakerCorrection';
import { applySpeakerCorrection, listSpeakerCorrections } from '../services/speakerCorrections';

interface SpeakerCorrectionState {
  isPanelOpen: boolean;
  pendingCorrectionId: string | null;
  corrections: SpeakerCorrectionRecord[];
  lastError: string | null;

  openPanel: () => void;
  closePanel: () => void;
  loadCorrections: (encounterId: string) => Promise<void>;
  applyCorrection: (encounterId: string, req: ApplyCorrectionRequest) => Promise<string>;
}

export const useSpeakerCorrectionStore = create<SpeakerCorrectionState>((set, get) => ({
  isPanelOpen: false,
  pendingCorrectionId: null,
  corrections: [],
  lastError: null,

  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),

  loadCorrections: async (encounterId) => {
    const rows = await listSpeakerCorrections(encounterId);
    set({ corrections: rows, lastError: null });
  },

  applyCorrection: async (encounterId, req) => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const optimistic: SpeakerCorrectionRecord = {
      id: tempId,
      actionType: req.actionType,
      physicianId: null,
      segmentIds: req.segmentIds,
      beforeState: {},
      afterState: {},
      skipReason: req.skip?.reason ?? null,
      judgeStatus: null,
      judgeReasoning: null,
      createdAt: new Date().toISOString(),
    };
    set({
      corrections: [...get().corrections, optimistic],
      pendingCorrectionId: tempId,
      lastError: null,
    });
    try {
      const { correctionId } = await applySpeakerCorrection(encounterId, req);
      set({
        corrections: get().corrections.map((c) =>
          c.id === tempId ? { ...c, id: correctionId } : c
        ),
        pendingCorrectionId: null,
      });
      return correctionId;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      set({
        corrections: get().corrections.filter((c) => c.id !== tempId),
        pendingCorrectionId: null,
        lastError: message,
      });
      throw err;
    }
  },
}));
